/**
 * LineFrames for alcove wall surfaces and alcove-corner chamfer diagonals,
 * derived from the canonical alcove ring so they always agree with the
 * resolved geometry.
 */
import type { AlcoveEdgeName, EdgeName, FloorPlanRoom, OuterWhich } from '../schemas';
import { type LineFrame, makeLineFrame } from './frames';
import { alcoveRingEdges } from './rings';

/**
 * LineFrame for one of the three alcove wall surfaces (left / back / right),
 * taken from the canonical ring so it always agrees with the resolved geometry —
 * including chamfered corners. Returns null if the alcove is absent, not an
 * alcove, or that wall is degenerate.
 */
export function alcoveWallLineFrame(
  shape: FloorPlanRoom,
  edgeName: EdgeName,
  alcoveIdx: number,
  wall: AlcoveEdgeName,
): LineFrame | null {
  const al = shape[edgeName]?.segments[alcoveIdx];
  if (!al || al.edgeSegmentType !== 'alcove') return null;
  const e = alcoveRingEdges(shape, edgeName, al).find(x => x.role.kind === 'wall' && x.role.wall === wall);
  return e ? makeLineFrame(e.a, e.b) : null;
}

/**
 * LineFrame for an outer-corner chamfer diagonal on an alcove (backLeftCorner / backRightCorner),
 * taken from the canonical ring (same single source as `alcoveWallLineFrame`) so its
 * direction follows boundary-walk order and `perp` points outward — matching how the
 * raw-scan converter frames these diagonals when it assigns segment positions.
 * Returns null if the alcove or corner doesn't exist, or the chamfer is degenerate.
 */
export function alcoveCornerLineFrame(
  shape: FloorPlanRoom,
  edgeName: EdgeName,
  alcoveIdx: number,
  which: OuterWhich,
): LineFrame | null {
  const al = shape[edgeName]?.segments[alcoveIdx];
  if (!al || al.edgeSegmentType !== 'alcove') return null;
  const ch = al[which];
  if (!ch || ch.dx < 1e-6 || ch.dy < 1e-6) return null;
  const e = alcoveRingEdges(shape, edgeName, al).find(x => x.role.kind === 'corner' && x.role.which === which);
  return e ? makeLineFrame(e.a, e.b) : null;
}
